<template>
  <div class="upload-file"
       v-loading.fullscreen.lock="fullscreenLoading"
       :element-loading-text="loadShowText"
  >
    <el-upload
      action="aaaa"
      :http-request="exeChoiceFile"
      :file-list="fileList"
      :show-file-list="false"
      class="upload-file-uploader"
      ref="fileUpload"

      :multiple="false"
    >
      <!-- 上传按钮 -->
      <slot name="button">
        <el-button type="default" v-show="showUpload" :disabled="props.disabled">上传</el-button>
      </slot>
    </el-upload>
    <!-- 上传提示 -->
    <div class="el-upload__tip" v-if="showTip">
      请上传
      <template v-if="fileSize">
        大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>
      </template>
      <template v-if="fileType">
        格式为 <b style="color: #f56c6c">{{ fileType.join('/') }}</b>
      </template>
      的文件
      <template v-if="limit">
        数量不超过 <b style="color: #f56c6c">{{ limit }} 个</b>
      </template>
    </div>
    <!-- 文件列表 -->
    <transition-group class="upload-file-list el-upload-list el-upload-list--text" name="el-fade-in-linear" tag="ul">
      <li :key="file.uid" class="el-upload-list__item ele-upload-list__item-content" v-for="(file, index) in fileList">
        <el-link :href="file.url" :underline="false" target="_blank">
          <span class="el-icon-document"> {{ getFileName(file.name) }} </span>
        </el-link>
        <div class="ele-upload-list__item-content-action">
          <el-link v-if="!props.disabled" :underline="false" @click="handleDelete(index)" type="danger"
                   style="white-space: nowrap">删除
          </el-link>
        </div>
      </li>
    </transition-group>
  </div>
</template>

<script setup>
import {computed} from 'vue';
import ChunkUpload from "./chunkUpload.js";
import {ElMessage} from "element-plus";

const props = defineProps({
  modelValue: [String, Object, Array],
  //上传的地址
  uploadUrl: {
    type: String,
    default: '/file/upload'  // /file/uploadCailuSxpte
  },
  // 数量限制
  limit: {
    type: Number,
    default: 10,
  },
  // 大小限制(MB)
  fileSize: {
    type: Number,
    default: 10,
  },
  // 文件类型, 例如['png', 'jpg', 'jpeg']
  fileType: {
    type: Array,
    default: () => ['doc', 'xls', 'ppt', 'txt', 'pdf'],
  },
  // 是否显示提示
  isShowTip: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showUpload: {
    type: Boolean,
    default: true,
  },
});

const {proxy} = getCurrentInstance();
const emit = defineEmits(['success', 'update:modelValue']);

const number = ref(0);
const uploadList = ref([]);

const fileList = ref([]);
const showTip = computed(() => props.isShowTip && (props.fileType || props.fileSize));

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      let temp = 1;
      // 首先将值转为数组
      const list = Array.isArray(val) ? val : props.modelValue.split(',');
      // 然后将数组转为对象数组
      fileList.value = list.map((item) => {
        if (typeof item === 'string') {
          item = {name: item, url: item};
        }
        item.uid = item.uid || new Date().getTime() + temp++;
        return item;
      });
    } else {
      fileList.value = [];
      return [];
    }
  },
  {deep: true, immediate: true}
);

const loadShowText = ref('')
const fullscreenLoading = ref(false)

async function exeChoiceFile(params) {
  const file = params.file;
  if (fileList.value.length + number.value + 1 > props.limit) {
    // 已经传了的 + 正在传的超过了，拦截
    proxy.$modal.msgError(`上传文件数量不能超过 ${props.limit} 个!`);
    return
  }

  // 校检文件类型
  if (props.fileType.length) {
    const fileName = file.name.split('.');
    const fileExt = fileName[fileName.length - 1].toLocaleLowerCase();
    const isTypeOk = props.fileType.indexOf(fileExt) >= 0;
    if (!isTypeOk) {
      proxy.$modal.msgError(`文件格式不正确, 请上传${props.fileType.join('/')}格式文件!`);
      return false;
    }
  }
  // 校检文件大小
  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      proxy.$modal.msgError(`上传文件大小不能超过 ${props.fileSize} MB!`);
      return false;
    }
  }

  try {
    fullscreenLoading.value = true
    number.value ++;
    const ttt = new ChunkUpload(progress => {
      loadShowText.value = `上传中 ${progress}%`
    })
    const res = await ttt.exeStartUpload(file)

    // 上传成功
    uploadList.value.push({ name: res.data.url, url: res.data.url });
    uploadedEnd();
    emit('success',file)
  } catch (e) {
    number.value --;
    fullscreenLoading.value = false
    proxy.$refs.fileUpload.handleRemove(file);
    uploadedEnd();
    ElMessage.error(e.message || '上传失败，请重试');
  }
}

// 上传结束处理
function uploadedEnd() {
  if (number.value > 0 && uploadList.value.length === number.value) {
    fileList.value = fileList.value.filter((f) => f.url !== undefined).concat(uploadList.value);
    uploadList.value = [];
    number.value = 0;
    emit('update:modelValue', listToString(fileList.value));
    fullscreenLoading.value = false
  }
  loadShowText.value = ''
}

// 删除文件
function handleDelete(index) {
  fileList.value.splice(index, 1);
  emit('update:modelValue', listToString(fileList.value));
}

// 获取文件名称
function getFileName(name) {
  if (name.lastIndexOf('/') > -1) {
    return name.slice(name.lastIndexOf('/') + 1);
  } else {
    return '';
  }
}

// 对象转成指定字符串分隔
function listToString(list, separator) {
  let strs = '';
  separator = separator || ',';
  for (let i in list) {
    if (list[i].url) {
      strs += list[i].url + separator;
    }
  }
  return strs != '' ? strs.substr(0, strs.length - 1) : '';
}

</script>

<style scoped lang="scss">
.upload-file-list .el-upload-list__item {
  border: 1px solid #e4e7ed;
  line-height: 2;
  margin-bottom: 10px;
  position: relative;
}

.upload-file-list .ele-upload-list__item-content {
  padding: 0 10px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: baseline;
  color: inherit;
}

.ele-upload-list__item-content-action .el-link {
  margin-right: 10px;
}
</style>
